import {
  Button,
  Divider,
  GeTrafficCamera,
  IconButton,
  NaChevronRight,
  NaClose,
  NaInfoCircle,
} from '@metservicedev/met-ui';
import React from 'react';
import { Navigate } from 'react-router-dom';

import { PortalInfo } from '../components/PortalInfo';
import { VerticalCollapse } from '../components/VerticalCollapse';
import { VonaLogo } from '../components/VonaLogo';
import { useAuth } from '../context/AuthProvider';
import { createStyles } from '../tss';
import { alpha } from '../util/alpha';

const useStyles = createStyles(({ classes, theme }) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    height: '100vh',
    width: '100vw',
    justifyContent: 'end',
    alignItems: 'center',
    background: 'url("/3562_Lloyd Homer_GNS Science.jpg")',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 4,
    background: alpha(theme.color.container.default, 0.8),
    color: theme.color.text.default,
    margin: theme.spacing(4),
    position: 'relative',
    overflow: 'hidden',
    width: 528,
    maxHeight: `calc(100% - ${theme.spacing(8)}px)`,
  },
  logo: {
    color: theme.color.icon.default,
    maxHeight: 40,
    margin: `${theme.spacing(3)}px ${theme.spacing(2)}px ${theme.spacing(2)}px`,
    flexShrink: 0,
    width: '100%',
  },
  question: {
    fontSize: '16px',
    fontWeight: '700',
  },
  answer: {
    fontSize: '12px',
    fontWeight: '400',
    marginBottom: theme.spacing(2),
  },
  signin: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  moreinfo: {
    padding: theme.spacing(2),
    paddingTop: 0,
  },
  infobutton: {
    color: theme.color.text.subtlest,
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: '700',
    gap: theme.spacing(2),
    background: 'unset',
    cursor: 'pointer',
    width: '100%',
    '&:hover': {
      color: theme.color.text.default,
    },
  },
  infobuttontext: {
    flex: 1,
    textAlign: 'left',
  },
  attribution: {
    position: 'fixed',
    top: theme.spacing(1),
    left: theme.spacing(1),
    color: theme.color.text.default,
    fontSize: '10px',
    fontWeight: 400,
    lineHeight: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    userSelect: 'none',
  },
  active: {},
  moreInfoIcon: {
    transition: 'transform 0.2s ease-in-out',
    [`&.${classes.active}`]: {
      transform: 'rotate(-90deg)',
    },
  },
  surface: {
    background: theme.color.container.default,
    margin: theme.spacing(2),
    borderRadius: 4,
    padding: theme.spacing(2),
    flex: 1,
    overflowY: 'auto',
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
}));

export const Landing: React.FC = () => {
  const { classes, cx } = useStyles();
  const auth = useAuth();
  const [showMoreInfo, setShowMoreInfo] = React.useState(false);

  if (auth.isAuthenticated) return <Navigate to="/" replace />;

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <VonaLogo className={classes.logo} />
        <Divider />
        <div className={classes.surface}>
          <VerticalCollapse open={!showMoreInfo}>
            <h4 className={classes.question}>Welcome to the VONA Input System </h4>
            <p className={classes.answer}>
              This service is for designated State Volcano Observatories as
              defined under ICAO Annex 3 to issue Volcano Observatory Notice
              to Aviation (VONA)
            </p>
            <div className={classes.signin}>
              <Button
                data-testid="button-log-in"
                disabled={auth?.isAuthenticated === undefined}
                onClick={() => auth.logIn()}
                variant="primary"
              >
                Sign in
              </Button>
            </div>
          </VerticalCollapse>
          <VerticalCollapse open={showMoreInfo}>
            <IconButton
              className={classes.closeButton}
              data-testid="button-close-more-info"
              icon={NaClose}
              onClick={() => setShowMoreInfo(false)}
              size="md"
              variant="tertiary"
            />
            <h4 className={classes.question}>VONA</h4>
            <p className={classes.answer}>
              Volcano Observatory Notice to Aviation, VONA, is a method of
              communicating volcano-related information between volcano
              observatories and their local meteorological and air traffic
              management service providers.
            </p>
            <p className={classes.answer}>
              To standardise the provision of this information a new
              requirement has been introduced by ICAO in Annex 3 to the
              Chicago Convention Meteorological Service for International
              Air Navigation, this will come into effect as a
              &quot;Recommended Practice&quot; in November 2025.
            </p>
            <p className={classes.answer}>
              Annex 3 requires States with active or potentially active
              volcanoes to designate a State Volcano Observatory (SVO) and
              task it to monitor those volcanoes and, when observing
              significant eruptive or pre-eruptive activity, to send
              information on that activity to their local air navigation and
              meteorological service providers, as well as their associated
              Volcanic Ash Advisory Centre (VAAC) via a Volcano Observatory
              Notice to Aviation (VONA).
            </p>
            <p className={classes.answer}>
              Recent changes introduced also require that this information
              is issued in ICAO Weather Exchange Model (IWXXM) format and
              disseminated over aeronautical fixed communication services
              (AFS) to be able to reach all users quickly and securely via
              standard aviation channels.
            </p>
            <h4 className={classes.question}>Development of VONA Input System</h4>
            <p className={classes.answer}>
              Development of the VONA input system supports and strengthens
              the continuing development of collaboration and partnership
              between the South Pacific regulatory, volcanological and
              meteorological agencies and their counterpart in New Zealand.
            </p>
            <p className={classes.answer}>
              Many South Pacific countries are unlikely to have the
              resources (expertise, equipment, software, and
              telecommunications infrastructure) to meet the new
              requirements. By developing the Volcano Observatory Notice to
              Aviation (VONA) input system, New Zealand (MetService) will be
              supporting Pacific Island countries uphold their obligations
              to the United Nations (UN) International Civil Aviation
              Organization (ICAO), maintain connectivity links (by ensuring
              safe passage of aviation and maritime connections) and
              continue to focus on building a resilient Pacific future.
            </p>
            <h4 className={classes.question}>Need help?</h4>
            <p className={classes.answer}>
              Contact the MetService Service Desk on +64 4 470 0700 or by email
              at servicedesk@metservice.com
            </p>
            <PortalInfo />
          </VerticalCollapse>
        </div>
        <div className={classes.moreinfo}>
          <button
            className={classes.infobutton}
            data-testid="button-toggle-more-info"
            onClick={() => setShowMoreInfo(!showMoreInfo)}
            type="button"
          >
            <NaInfoCircle size={20} />
            <span className={classes.infobuttontext}>Need more information?</span>
            <NaChevronRight size={20} className={cx(classes.moreInfoIcon, { [classes.active]: showMoreInfo })} />
          </button>
        </div>
      </div>
      <span className={classes.attribution}>
        <GeTrafficCamera size={24} />
        &quot;Mount Ruapehu in eruption June 1996&quot;<br />
        Photo by Lloyd Homer, GNS Science
      </span>
    </div>
  );
};
